/**
 * Inventory page hero/state derivation.
 *
 * Distinguishes:
 *  - First-sync genuinely in progress (creds present, no ingest yet) → animated banner
 *  - Stalled placeholder (creds absent — OAuth never completed, NEA-6043)  → Connect CTA
 *  - No POS source at all                                                  → Connect CTA
 *  - Healthy synced state                                                  → catalogue cards
 *
 * "Stalled" only applies to OAuth and plug-play providers — those are the two
 * connect types that store credentials on the inventory-source row, so an
 * empty credentials map there means a real failure. partner-lead and the
 * default branch (FTP, email ingest, HTTP upload, etc.) authenticate externally
 * via `shop.uploadKey` / host-level FTP credentials / POS-side push, and
 * legitimately carry no credentials on the row — they must NOT be flagged as
 * stalled during the window between sign-up and first ingest.
 *
 * `undefined` `hasCredentials` (older API responses pre-deploy) is intentionally
 * treated as unknown — keeps the existing first-sync behaviour.
 */
const CREDS_BEARING_CONNECT_TYPES = new Set(['oauth', 'plug-play']);

export const deriveHeroState = ({ shop, summary, summaryLoading, shopReady }) => {
	const source = summary?.source ?? null;
	const sourceLabel = source?.label ?? null;
	const hasSource = !!source;

	const credsExpected = CREDS_BEARING_CONNECT_TYPES.has(source?.connectType);
	const stalledNoCredentials = hasSource && credsExpected && source?.hasCredentials === false && !source?.lastSyncAt;
	const firstSyncInProgress = hasSource && !source?.lastSyncAt && !stalledNoCredentials;
	const noSource = (shopReady && !shop?.stockInventorySource && !hasSource) || stalledNoCredentials;
	// The catalogue block (ConnectionStatusCard + visibility cards) is only
	// meaningful once a real ingest has happened. Tied off here so the JSX
	// can't drift into rendering it underneath the stalled-placeholder
	// Connect CTA — that exact dual-render was the bug NEA-6043 set out
	// to hide. Tested in isolation; the JSX consumes the flag verbatim.
	const showCatalogue = !summaryLoading && hasSource && !firstSyncInProgress && !stalledNoCredentials;

	return {
		source,
		sourceLabel,
		hasSource,
		stalledNoCredentials,
		firstSyncInProgress,
		noSource,
		showCatalogue,
		summaryLoading
	};
};
